import React, { useCallback, useEffect, useState, useRef, useMemo } from "react"
import Observer from "@researchgate/react-intersection-observer"
import { graphql } from "gatsby"

import Article from "components/article"
import Navigation from "components/article/navigation"
import Loader from "components/loader"
import HtlScript from "components/htl-script"

import usePersonalizedArticles from "hooks/usePersonalizedArticles"
import useArticleScrollTracking from "hooks/useArticleScrollTracking"
import { parseArticle } from "utils/parseArticle"
import useScroll from "utils/useScroll"
import {
  MidpointMarker,
  LoaderWrapper,
  ObserverContainer,
  MidPointObserverContainer,
  ArticleFullContainer,
} from "components/article/styles"
import useUserAgent from "hooks/useUserAgent"
import useAdTargeting from "components/article/hooks/useAdTargeting"

const MAX_LOADED_ARTICLES = 5
const SCROLL_LOAD_MARGIN = "20%"
const INTERSECTION_THRESHOLDS = [0, 0.1, 0.2, 0.3, 0.4, 0.5, 0.6, 0.7, 0.8, 0.9, 1]

const ArticleObserver = ({ onChange, children }) => {
  // Get the navigation height to set the rootMargin
  const [navHeight, setNavHeight] = useState(0)

  useEffect(() => {
    if (typeof window !== "undefined") {
      const nav = document.querySelector("#article-navigation")
      if (nav) {
        setNavHeight(nav.offsetHeight)
      }

      // Add resize listener to update navHeight if window is resized
      const handleResize = () => {
        const navElement = document.querySelector("#article-navigation")
        if (navElement) {
          setNavHeight(navElement.offsetHeight)
        }
      }

      window.addEventListener("resize", handleResize)
      return () => {
        window.removeEventListener("resize", handleResize)
      }
    }
  }, [])

  // Set rootMargin to create a gap of 50-100px before calling newPage()
  // This ensures the red line is well into the viewport before triggering
  const rootMargin = "50px 0px 0px 0px"

  return (
    <Observer onChange={onChange} threshold={INTERSECTION_THRESHOLDS} rootMargin={rootMargin}>
      {children}
    </Observer>
  )
}

const BottomArticleObserver = ({ onChange, children }) => (
  <Observer onChange={onChange} threshold={[0.1, 0.5]} rootMargin="0px 0px 200px 0px">
    {children}
  </Observer>
)

const ArticleWrapper = ({
  article,
  index,
  articleRef,
  helpers,
  visibleArticles,
  handleArticleVisibilityChange,
  loadNextArticle,
  isLoadingMore,
  loadTriggeredArticles,
  isScrollingDown,
  loadedArticles,
  analyticsInitialized,
  handleBottomObserverChange,
  setAnalyticsInitialized,
  loadedArticlesPromotion,
  handleLoadedArticlesPromotion,
  hasAdsOnly,
  setHasAdsOnly,
  isCurrentArticle,
  htlScriptLoaded,
  handleNewPage,
  lastTargetedArticleId,
  shouldSkipInfiniteScroll,
}) => {
  const parsedArticle = useMemo(() => parseArticle(article, helpers), [article.id, helpers])
  const isSearchBot = useUserAgent()
  const { direction } = useScroll()

  const lastNewPageCall = useRef(null)
  const targetingOptions = useAdTargeting(article)

  const [navHeight, setNavHeight] = useState(0)

  useEffect(() => {
    if (typeof window !== "undefined") {
      const nav = document.querySelector("#article-navigation")
      if (nav) {
        setNavHeight(nav.offsetHeight)
      }
    }
  }, [])

  useEffect(() => {
    if (index === 0) {
      window.htlbid = window.htlbid || {}
      window.htlbid.cmd = window.htlbid.cmd || []
      window.htlbid.cmd.push(async () => {
        try {
          Object.keys(targetingOptions).forEach((key) => {
            window.htlbid.setTargeting(key, targetingOptions[key])
          })
        } catch (error) {
          console.error("Error refreshing ads:", error)
        }
      })
    }
  }, [])

  const handleTopIntersectionChange = useCallback(
    (entry) => {
      const isScrollingUp = direction === "up"

      if (
        (!entry.isIntersecting &&
          entry.intersectionRatio === 0 &&
          entry.boundingClientRect.top < 0 &&
          !isScrollingUp) ||
        (entry.isIntersecting && entry.intersectionRatio === 1 && isScrollingUp)
      ) {
        if (
          htlScriptLoaded &&
          window.htlbid &&
          lastNewPageCall.current !== article.id &&
          index !== 0
        ) {
          const articleId = article.id

          const executeAdRefresh = () => {
            window.htlbid.cmd.push(async () => {
              try {
                window.htlbid.setTargeting("post_id", articleId)
                Object.keys(targetingOptions).forEach((key) => {
                  if (targetingOptions[key]) {
                    window.htlbid.setTargeting(key, targetingOptions[key])
                  }
                })
                await window.htlbid.newPage()
                lastNewPageCall.current = articleId
              } catch (error) {
                console.error("Error refreshing ads:", error)
              }
            })
          }

          executeAdRefresh()
        }
      }

      const visibilityThreshold = isScrollingUp ? 0.6 : 0.4
      if (entry.intersectionRatio > visibilityThreshold) {
        handleArticleVisibilityChange(entry, article.id)
      }
    },
    [article.id, handleArticleVisibilityChange, htlScriptLoaded, navHeight, direction],
  )

  const handleBottomIntersectionChange = useCallback(
    (entry) => {
      if (entry.isIntersecting && entry.intersectionRatio > 0.1) {
        if (lastNewPageCall.current === article.id) {
          lastNewPageCall.current = null
        }

        const shouldLoadMore =
          !shouldSkipInfiniteScroll &&
          isScrollingDown.current &&
          !isLoadingMore &&
          loadedArticles.length < MAX_LOADED_ARTICLES &&
          !loadTriggeredArticles.current.has(article.id)

        if (shouldLoadMore) {
          try {
            loadTriggeredArticles.current.add(article.id)

            loadNextArticle(isSearchBot)
          } catch (err) {
            console.error("Error loading next article:", err)
          }
        }
      }
    },
    [
      article.id,
      isLoadingMore,
      loadNextArticle,
      loadedArticles.length,
      loadTriggeredArticles,
      isScrollingDown,
      isSearchBot,
    ],
  )

  const handleMidpointIntersection = useCallback(
    (entry) => {
      if (entry.intersectionRatio >= 0.5 && !analyticsInitialized) {
        handleBottomObserverChange(entry)
        setAnalyticsInitialized(true)
      }
    },
    [analyticsInitialized, handleBottomObserverChange, setAnalyticsInitialized],
  )

  return (
    <ArticleFullContainer id={`article-wrapper-${article.id}`} ref={articleRef}>
      <div style={{ position: "relative", width: "100%" }}>
        <ObserverContainer
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "1px",
          }}
          data-article-id={article.id}
          data-observer-type="top"
        >
          <ArticleObserver onChange={handleTopIntersectionChange}>
            <MidpointMarker />
          </ArticleObserver>
        </ObserverContainer>
      </div>

      <Article
        isTop={index === 0}
        index={index}
        article={parsedArticle}
        contentfulPromotionalPlacements={helpers.contentfulPromotionalPlacements}
        isVisible={visibleArticles.has(article.id)}
        loadedArticlesPromotion={loadedArticlesPromotion}
        handleLoadedArticlesPromotion={handleLoadedArticlesPromotion}
        hasAdsOnly={hasAdsOnly}
        setHasAdsOnly={setHasAdsOnly}
        isCurrentArticle={isCurrentArticle}
        htlScriptLoaded={htlScriptLoaded}
        handleNewPage={handleNewPage}
        lastTargetedArticleId={lastTargetedArticleId}
      />

      <div style={{ position: "relative", width: "100%" }}>
        <ObserverContainer
          style={{
            position: "absolute",
            bottom: 0,
            left: 0,
            width: "100%",
            height: "1px",
          }}
          data-article-id={article.id}
          data-observer-type="bottom"
        >
          <BottomArticleObserver onChange={handleBottomIntersectionChange}>
            <MidpointMarker />
          </BottomArticleObserver>
        </ObserverContainer>
      </div>

      {index === 0 && (
        <MidPointObserverContainer>
          <Observer onChange={handleMidpointIntersection} threshold={[0.5]} rootMargin="0px">
            <MidpointMarker />
          </Observer>
        </MidPointObserverContainer>
      )}
    </ArticleFullContainer>
  )
}

const useUrlAndTitleUpdate = (loadedArticles, currentArticleIndex) => {
  const updateUrlAndTitle = useCallback(
    (article) => {
      if (!article?.url) {
        console.error("Missing URL for article:", article?.id)
        return
      }

      const currentPath = typeof window !== "undefined" ? window.location.pathname : ""

      if (article.titles?.site) {
        document.title = article.titles.site
      }

      if (!currentPath.match(article.url)) {
        try {
          window.history.replaceState(
            {
              articleId: article.id,
              articleIndex: currentArticleIndex,
            },
            document.title,
            article.url,
          )

          if (typeof window !== "undefined" && window.dataLayer) {
            window.dataLayer.push({
              event: "virtualPageview",
              virtualPageURL: article.url,
              virtualPageTitle: article.titles?.site,
              articleIndex: currentArticleIndex,
            })
          }
        } catch (err) {
          console.error("Failed to update URL:", err)
        }
      }
    },
    [currentArticleIndex],
  )

  useEffect(() => {
    if (loadedArticles && loadedArticles.length > currentArticleIndex) {
      const currentArticle = loadedArticles[currentArticleIndex]
      updateUrlAndTitle(currentArticle)
    }
  }, [currentArticleIndex, loadedArticles, updateUrlAndTitle])
}

const ArticlePage = (props) => {
  const {
    data: {
      article: initialArticle,
      allArticleFeaturedVideo: { nodes: featuredVideos },
      allTrustedCitationSource: { nodes: trustedCitationSources },
      allContentfulAdPlacement: { nodes: contentfulAdPlacements },
      allContentfulPromotionalPlacement: { nodes: contentfulPromotionalPlacements },
      allArticle: { nodes: allArticles },
    },
  } = props
  const shouldSkipInfiniteScroll = initialArticle.template === "clean"
  const [visibleArticles, setVisibleArticles] = useState(new Set([initialArticle.id]))
  const [analyticsInitialized, setAnalyticsInitialized] = useState(false)
  const [currentArticleIndex, setCurrentArticleIndex] = useState(0)
  const [htlScriptLoaded, setHtlScriptLoaded] = useState(false)
  const [hasAdsOnly, setHasAdsOnly] = useState(false)

  const loadTriggeredArticles = useRef(new Set())
  const articleRefs = useRef(new Map())

  const helpers = useMemo(
    () => ({
      featuredVideos,
      trustedCitationSources,
      contentfulAdPlacements,
      contentfulPromotionalPlacements,
    }),
    [
      featuredVideos,
      trustedCitationSources,
      contentfulAdPlacements,
      contentfulPromotionalPlacements,
    ],
  )

  const parsedInitialArticle = useMemo(
    () => parseArticle(initialArticle, helpers),
    [initialArticle, helpers],
  )

  const { loadNextArticle, isLoadingMore, isLoading, loadedArticles, getAnalyticsId } =
    usePersonalizedArticles({
      articleId: parsedInitialArticle.id,
      initialArticle: parsedInitialArticle,
      allArticles,
    })

  const { isScrollingDown } = useArticleScrollTracking(
    loadedArticles || [parsedInitialArticle],
    currentArticleIndex,
    setCurrentArticleIndex,
  )
  useUrlAndTitleUpdate(loadedArticles || [parsedInitialArticle], currentArticleIndex)

  useEffect(() => {
    if (!loadedArticles) return

    loadedArticles.forEach((article) => {
      if (!articleRefs.current[article.id]) {
        articleRefs.current[article.id] = React.createRef()
      }
    })
  }, [loadedArticles])

  const handleArticleVisibilityChange = useCallback(
    (entry, articleId) => {
      if (!loadedArticles) return

      const { intersectionRatio, boundingClientRect } = entry
      const scrollingUp = boundingClientRect.top > 0

      // Use different thresholds based on scroll direction
      // More conservative when scrolling up to prevent bouncing
      const activeThreshold = scrollingUp ? 0.7 : 0.5
      const deactiveThreshold = scrollingUp ? 0.1 : 0.2

      // Add debounce for scrolling up to prevent rapid changes
      if (scrollingUp) {
        // If we're scrolling up, we need to be more careful about changing the current article
        if (intersectionRatio >= activeThreshold) {
          setVisibleArticles((prev) => new Set(prev).add(articleId))

          const index = loadedArticles.findIndex((article) => article.id === articleId)
          if (index !== -1 && index < currentArticleIndex) {
            // Add a small delay when scrolling up to ensure stability
            setTimeout(() => {
              // Double-check we still want to make this change
              const element = document.getElementById(`article-wrapper-${articleId}`)
              if (element && element.getBoundingClientRect().top >= 0) {
                setCurrentArticleIndex(index)
              }
            }, 50)
          }
        } else if (intersectionRatio < deactiveThreshold) {
          setVisibleArticles((prev) => {
            const next = new Set(prev)
            next.delete(articleId)
            return next
          })
        }
      } else if (intersectionRatio >= activeThreshold) {
        // Original behavior for scrolling down
        setVisibleArticles((prev) => new Set(prev).add(articleId))

        const index = loadedArticles.findIndex((article) => article.id === articleId)
        if (index !== -1 && index > currentArticleIndex) {
          setCurrentArticleIndex(index)
        }
      } else if (intersectionRatio < deactiveThreshold) {
        setVisibleArticles((prev) => {
          const next = new Set(prev)
          next.delete(articleId)
          return next
        })
      }
    },
    [loadedArticles, currentArticleIndex, htlScriptLoaded],
  )

  const handleBottomObserverChange = useCallback(async () => {
    try {
      await getAnalyticsId()
    } catch (err) {
      console.error("Failed to get analytics ID:", err)
    }
  }, [getAnalyticsId])

  const handleLoadedArticlesPromotion = () => {
    setHasAdsOnly(true)
  }

  const currentArticle = loadedArticles?.[0] || parsedInitialArticle
  const isFeatured = currentArticle.template === "feature"
  const isPremium = currentArticle.template === "premium"
  const isClean = currentArticle.template === "clean"
  const isStandard = !isFeatured && !isPremium && !isClean
  const bodyBackground = isPremium && currentArticle.bodyColor ? currentArticle.bodyColor : "#fff"
  const isTranslucent = isPremium && currentArticle.navigationTransparency !== "solid"
  const lastTargetedArticleId = useRef(null)

  return (
    <>
      <HtlScript
        onLoad={() => {
          setHtlScriptLoaded(true)
        }}
        targetingOptions={useAdTargeting(initialArticle)}
      />
      <Navigation
        id="article-navigation"
        article={loadedArticles[currentArticleIndex] || parsedInitialArticle}
        bodyBackground={bodyBackground}
        isTranslucent={isTranslucent}
      />

      {loadedArticles.map((article, index) => {
        const articleRef = articleRefs.current[article.id]

        return (
          <ArticleWrapper
            key={article.id}
            article={article}
            shouldSkipInfiniteScroll={shouldSkipInfiniteScroll}
            index={index}
            articleRef={articleRef}
            helpers={helpers}
            visibleArticles={visibleArticles}
            handleArticleVisibilityChange={handleArticleVisibilityChange}
            loadNextArticle={loadNextArticle}
            isLoadingMore={isLoadingMore}
            loadTriggeredArticles={loadTriggeredArticles}
            isScrollingDown={isScrollingDown}
            loadedArticles={loadedArticles}
            analyticsInitialized={analyticsInitialized}
            handleBottomObserverChange={handleBottomObserverChange}
            setAnalyticsInitialized={setAnalyticsInitialized}
            handleLoadedArticlesPromotion={handleLoadedArticlesPromotion}
            hasAdsOnly={hasAdsOnly}
            setHasAdsOnly={setHasAdsOnly}
            isCurrentArticle={index === currentArticleIndex}
            htlScriptLoaded={htlScriptLoaded}
            lastTargetedArticleId={lastTargetedArticleId}
          />
        )
      })}

      {(isLoading || isLoadingMore) && (
        <LoaderWrapper>
          <Loader width={40} height={40} />
        </LoaderWrapper>
      )}
    </>
  )
}

export default ArticlePage

export const query = graphql`
  query ($id: String) {
    article(id: { eq: $id }) {
      ... on Article {
        ...articleFields
      }
    }
    allArticleFeaturedVideo {
      nodes {
        ... on ArticleFeaturedVideo {
          ...articleFeaturedVideoFields
        }
      }
    }
    allTrustedCitationSource {
      nodes {
        ... on TrustedCitationSource {
          ...trustedCitationSourceFields
        }
      }
    }
    allContentfulAdPlacement(filter: { active: { eq: true } }) {
      nodes {
        ... on ContentfulAdPlacement {
          ...adPlacementFields
        }
      }
    }
    allContentfulPromotionalPlacement(
      filter: { active: { eq: true }, targetPage: { eq: "Article" } }
    ) {
      nodes {
        ... on ContentfulPromotionalPlacement {
          ...promotionalPlacementFields
        }
      }
    }
    allArticle(limit: 75, sort: { publishDate: DESC }) {
      nodes {
        ... on Article {
          id
          publishDate
        }
      }
    }
  }
`
// export async function config() {
async function config() {
  const { data } = graphql`
    {
      articles: allArticle(filter: { indexForSearch: { eq: false } }) {
        nodes {
          body
          publishDate
          url
        }
      }
    }
  `
  /**
   * We will set defer to true for DSG for articles that are
   * - not indexed for search (handled by filter)
   * - were published more than 30 days ago
   * - and do not have an editor-placed product in them
   */
  const actualDate = new Date()
  const maxDate = new Date(new Date().setDate(actualDate.getDate() - 30))
  const articles = new Set(
    data.articles.nodes
      .filter((article) => {
        const editorPlacedProduct = article.body.some((element) => {
          const bodyElement = JSON.parse(element)
          const isProductType =
            bodyElement.type === "product" || bodyElement.type === "text-and-product"
          return isProductType && !bodyElement.autoplacement
        })
        return article.publishDate < maxDate.toISOString() && !editorPlacedProduct
      })
      .map(({ url }) => url),
  )

  return ({ params }) => ({
    defer: articles.has(params.url),
  })
}
